import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Store, Plus, Trash2, Pencil, Warehouse } from "lucide-react";
import { Input, TextArea } from "./Warehouse";

const EMPTY = { name: "", city: "", province: "", address: "", work_in: "" };

export default function AdminBranches() {
  const [rows, setRows] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelId, setConfirmDelId] = useState(null);
  const [confirmCentralId, setConfirmCentralId] = useState(null);

  const makeCentral = async (id) => {
    try {
      const { data } = await api.post(`/branches/${id}/make-central`);
      toast.success(`Dijadikan gudang pusat — ${data.moved_qty} stok dilebur, ${data.staff_moved} staff dialihkan`);
      setConfirmCentralId(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  const load = async () => setRows((await api.get("/branches")).data);
  useEffect(() => {
    load();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/shipping-info`).then((r) => r.json()).then((d) => setProvinces(d.provinces || [])).catch(() => {});
  }, []);

  const startEdit = (b) => {
    setEditId(b.id);
    setForm({ name: b.name, city: b.city, province: b.province || "", address: b.address || "", work_in: b.work_in || "" });
    setShow(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.patch(`/branches/${editId}`, form);
        toast.success("Cabang diperbarui");
      } else {
        await api.post("/branches", form);
        toast.success("Cabang dibuat");
      }
      setForm(EMPTY); setShow(false); setEditId(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/branches/${id}`);
      toast.success("Cabang dihapus");
      setConfirmDelId(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menghapus cabang"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Manajemen</p>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Cabang</h1>
        </div>
        <button data-testid="new-branch-btn" onClick={() => { setShow(!show); if (show) { setEditId(null); setForm(EMPTY); } }} className="h-11 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex items-center gap-2 active:scale-[0.98] transition-transform">
          <Plus size={16} /> Baru
        </button>
      </div>

      {show && (
        <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <Input testid="b-name" label="Nama Cabang" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <div className="grid grid-cols-2 gap-3">
            <Input testid="b-city" label="Kota" value={form.city} onChange={(v) => setForm({ ...form, city: v })} required />
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Provinsi</span>
              <select data-testid="b-province" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-300 text-sm bg-white">
                <option value="">Pilih provinsi</option>
                {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
          </div>
          <TextArea testid="b-addr" label="Alamat" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Jam Masuk Khusus Cabang Ini (opsional)</span>
            <input data-testid="b-work-in" type="time" value={form.work_in} onChange={(e) => setForm({ ...form, work_in: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-lg border border-slate-300 text-sm" />
          </label>
          <p className="text-[11px] text-slate-400">Provinsi & kota dipakai untuk aturan gratis ongkir. Jam masuk kosong = ikuti jam global cabang di Pengaturan.</p>
          <button data-testid="b-submit" type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl">{editId ? "Simpan Perubahan" : "Simpan"}</button>
        </form>
      )}

      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-slate-500">Belum ada cabang.</p>}
        {rows.map((b) => (
          <div key={b.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start justify-between gap-3">
            <div className="flex gap-3 min-w-0">
              <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0"><Store size={20} /></div>
              <div className="min-w-0">
                <p className="font-bold text-slate-900" style={{fontFamily:'Manrope'}}>{b.name}</p>
                <p className="text-xs text-slate-600">{b.city}{b.province ? `, ${b.province}` : ""}{b.work_in ? ` · Masuk ${b.work_in}` : ""}</p>
                {b.address && <p className="text-xs text-slate-500 mt-0.5">{b.address}</p>}
              </div>
            </div>
            <div className="flex flex-col gap-1.5 flex-shrink-0 items-end">
              <div className="flex gap-1.5">
                <button data-testid={`edit-b-${b.id}`} onClick={() => startEdit(b)} className="w-10 h-10 rounded-lg text-blue-600 hover:bg-blue-50 flex items-center justify-center" title="Edit cabang"><Pencil size={16} /></button>
                {confirmCentralId === b.id ? (
                  <div className="flex gap-2">
                    <button data-testid={`confirm-central-b-${b.id}`} onClick={() => makeCentral(b.id)} className="h-10 px-3 rounded-lg bg-amber-500 text-white text-xs font-bold active:scale-95 transition-transform">Ya, Jadikan Pusat</button>
                    <button data-testid={`cancel-central-b-${b.id}`} onClick={() => setConfirmCentralId(null)} className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 text-xs font-bold">Batal</button>
                  </div>
                ) : (
                  <button data-testid={`central-b-${b.id}`} onClick={() => setConfirmCentralId(b.id)} className="w-10 h-10 rounded-lg text-amber-600 hover:bg-amber-50 flex items-center justify-center" title="Jadikan gudang pusat (stok & staff dilebur)"><Warehouse size={16} /></button>
                )}
                {confirmDelId === b.id ? (
                  <div className="flex gap-2">
                    <button data-testid={`confirm-del-b-${b.id}`} onClick={() => del(b.id)} className="h-10 px-3 rounded-lg bg-red-600 text-white text-xs font-bold active:scale-95 transition-transform">Ya, Hapus</button>
                    <button data-testid={`cancel-del-b-${b.id}`} onClick={() => setConfirmDelId(null)} className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 text-xs font-bold">Batal</button>
                  </div>
                ) : (
                  <button data-testid={`del-b-${b.id}`} onClick={() => setConfirmDelId(b.id)} className="w-10 h-10 rounded-lg text-red-600 hover:bg-red-50 flex items-center justify-center"><Trash2 size={16} /></button>
                )}
              </div>
              {confirmCentralId === b.id && <p className="text-[10px] text-amber-700 max-w-[200px] text-right">Stok dilebur ke gudang pusat, staff cabang jadi staff gudang, cabang dihapus.</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
